import { minBy } from 'es-toolkit';
import React, { useEffect, useState } from 'react';

import { CalculatedWebJoint, WebbingInfo } from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import {
  snapMousePositionDuringProfileInsertion,
  updateMousyInsertionProfile,
} from '../../library/profiles/mousyProfileSnapping';
import { isFullProfile } from '../../library/profiles/profileManipulation';
import { AppDispatch, RootState } from '../../store';
import { addProfileLineToProfile, createWebSegmentAndRewebTruss } from '../../store/actions/profileActions';
import { resetMousyInsertionProfile, useMousyProfile } from '../../store/mousyInsertionProfileStore';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getComponentDesignInsertionInfoOrNull } from '../../store/selectors/insertionObjectSelectors';
import {
  getMousyProfileInsertionInformation,
  isMousyProfileInsertionActive,
} from '../../store/selectors/mousyProfileInsertionSelectors';
import {
  getInsertingOrRewebbingProfileOrNull,
  getInsertingProfileTrussSettingsOrNull,
  getShouldRenderProfile,
  isInsertingAnAtticTruss,
  isInsertingANonStructuralGable,
} from '../../store/selectors/profileSelectors';
import { getActiveWebbingInfo } from '../../store/selectors/webbingInfoSelectors';
import { GeneratableBearingVisual } from '../component/BearingVisual';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { CalculatedWebJointVisual } from './CalculatedWebJointVisual';
import { ProfileJointVisual } from './ProfileJointVisual';
import { ProfileLineVisual } from './ProfileLineContainer';
import { WebSegmentPreviewVisual } from './WebSegmentPreviewVisual';
import { WebSegmentVisual } from './WebSegmentVisual';

const onMouseMove = (dispatch: AppDispatch) => (event: GlobalThreeJSDomObjectEvent) => {
  dispatch((_, getState) => {
    const mousyProfileInsertionInformation = getMousyProfileInsertionInformation(getState());
    if (mousyProfileInsertionInformation) {
      updateMousyInsertionProfile(event.eventPosition.worldPosition, mousyProfileInsertionInformation);
    }
  });
};

const onClick = (dispatch: AppDispatch) => (event: GlobalThreeJSDomObjectEvent) =>
  dispatch((dispatch, getState) => {
    const mousyProfileInsertionInformation = getMousyProfileInsertionInformation(getState());

    if (mousyProfileInsertionInformation) {
      const { profile, topChordWidth, snappingLevel } = mousyProfileInsertionInformation;
      const eventPosition = event.eventPosition.worldPosition;

      const snapResult = snapMousePositionDuringProfileInsertion(profile, eventPosition, topChordWidth, snappingLevel);
      if (snapResult) {
        dispatch(addProfileLineToProfile(snapResult.distance, snapResult.slope, profile));
        return false; // stop propagation
      }
    }
  });

const subscribeToSceneEvents = (dispatch: AppDispatch) => {
  const move = onMouseMove(dispatch);
  const click = onClick(dispatch);

  threeJsDom.addGlobalListener('mousemove', move);
  threeJsDom.addGlobalListener('mouseleave', resetMousyInsertionProfile);
  threeJsDom.addGlobalListener('click', click, 2);

  return () => {
    threeJsDom.removeGlobalListener('mousemove', move);
    threeJsDom.removeGlobalListener('mouseleave', resetMousyInsertionProfile);
    threeJsDom.removeGlobalListener('click', click);
  };
};

export function ProfileVisual() {
  const dispatch = useAppDispatch();

  const profile = useAppSelector(getInsertingOrRewebbingProfileOrNull);
  const enableMousyInsertion = useAppSelector(isMousyProfileInsertionActive);
  const span = useAppSelector(getInsertingProfileTrussSettingsOrNull)?.span;
  const shouldRenderProfile = useAppSelector(getShouldRenderProfile);
  const shouldRenderBearings = !useAppSelector(getActiveComponentDesign);
  const shouldShowWebbingVisuals = useAppSelector((state: RootState) => {
    if (state.application.rewebbingInfo) return true;
    const componentDesignInsertionInfo = getComponentDesignInsertionInfoOrNull(state);
    return (
      componentDesignInsertionInfo?.definitionType === 'Profile' &&
      componentDesignInsertionInfo.stage === 'StructuralDetails' &&
      !isInsertingANonStructuralGable(state) &&
      !isInsertingAnAtticTruss(state)
    );
  });
  const webbingInfo = useAppSelector(getActiveWebbingInfo);

  const mousyInsertionProfile = useMousyProfile();

  const registerForRaycasting = !enableMousyInsertion || !mousyInsertionProfile;

  const profileToRender =
    profile && shouldRenderProfile ? (enableMousyInsertion && mousyInsertionProfile) || profile : null;

  useEffect(() => {
    if (profileToRender && enableMousyInsertion) return subscribeToSceneEvents(dispatch);
  }, [profileToRender, enableMousyInsertion, dispatch]);

  if (!profileToRender) return null;

  const profileJoints = profileToRender.profileJoints.map((joint) => (
    <ProfileJointVisual key={joint.guid} joint={joint} registerForRaycasting={registerForRaycasting} />
  ));

  const profileLines = profileToRender.profileLines.map((line) => (
    <ProfileLineVisual
      key={line.guid}
      profile={profileToRender}
      profileLine={line}
      registerForRaycasting={registerForRaycasting}
      shouldShowWebbingVisuals={shouldShowWebbingVisuals}
    />
  ));

  const bearingVisuals =
    shouldRenderBearings &&
    isFullProfile(profileToRender) &&
    profileToRender.generatableBearings.length > 0 &&
    span != null
      ? profileToRender.generatableBearings
          .map((bearing) => <GeneratableBearingVisual key={bearing.guid} bearing={bearing} span={span} />)
          .filter(notNullish)
      : null;

  return (
    <group>
      {profileJoints}
      {profileLines}
      {bearingVisuals}
      {/* Note that we need to make sure that this React component gets unmounted when not in use in order to prevent */}
      {/* its state from becoming stale. */}
      {webbingInfo ? <ProfileWebbingVisual webbingInfo={webbingInfo} /> : null}
    </group>
  );
}

interface CalculatedWebJointInfo {
  readonly joint: CalculatedWebJoint;
  readonly webbingRegionIndex: number;
}

function isKWebJoint(joint: CalculatedWebJoint) {
  return joint.jointType === 'KWebNonStepSide' || joint.jointType === 'KWebStepSide';
}

function isWebSegmentValid(
  jointAInfo: CalculatedWebJointInfo,
  jointBInfo: CalculatedWebJointInfo,
  webbingInfo: WebbingInfo,
) {
  if (jointAInfo.webbingRegionIndex !== jointBInfo.webbingRegionIndex) return false;

  const { joint: jointA } = jointAInfo;
  const { joint: jointB } = jointBInfo;
  if (jointA.guid === jointB.guid) return false;
  if (!isKWebJoint(jointA) && !isKWebJoint(jointB) && jointA.chordType === jointB.chordType) return false;

  if (
    webbingInfo.webbingRegions
      .flatMap((region) => region.webSegments)
      .some(
        (webSegment) =>
          (webSegment.baseJointGuid === jointA.guid && webSegment.endJointGuid === jointB.guid) ||
          (webSegment.baseJointGuid === jointB.guid && webSegment.endJointGuid === jointA.guid),
      )
  ) {
    return false;
  }

  return true;
}

interface ProfileWebbingVisualProps {
  readonly webbingInfo: WebbingInfo | null;
}

const JOINT_CLICK_RADIUS = 1;

function ProfileWebbingVisual({ webbingInfo }: ProfileWebbingVisualProps) {
  const dispatch = useAppDispatch();

  // Note that we need to make sure that this React component gets unmounted when not in use in order to prevent this
  // state from becoming stale.
  const [selectedJointInfo, setSelectedJointInfo] = useState<CalculatedWebJointInfo | null>(null);

  useEffect(() => {
    function onClick(event: GlobalThreeJSDomObjectEvent) {
      if (!webbingInfo) return;

      const clickLocation = Point2D.fromObject(event.eventPosition.worldPosition);

      const jointInfos = webbingInfo.webbingRegions.flatMap((webbingRegion, webbingRegionIndex) =>
        webbingRegion.calculatedWebJoints.map((joint) => ({ joint, webbingRegionIndex })),
      );
      const closestJointInfo = minBy(jointInfos, ({ joint }) => clickLocation.distanceTo(joint.location));
      if (!closestJointInfo) return;

      if (clickLocation.distanceTo(closestJointInfo.joint.location) <= JOINT_CLICK_RADIUS) {
        // The joint has been clicked
        if (selectedJointInfo == null) {
          setSelectedJointInfo(closestJointInfo);
        } else {
          if (!isWebSegmentValid(selectedJointInfo, closestJointInfo, webbingInfo)) return;

          setSelectedJointInfo(null);

          dispatch(
            createWebSegmentAndRewebTruss(
              closestJointInfo.webbingRegionIndex,
              selectedJointInfo.joint.guid,
              closestJointInfo.joint.guid,
            ),
          );
        }
        return false; // stop propagation
      } else {
        // No joint is being clicked
        if (selectedJointInfo) {
          setSelectedJointInfo(null);
          return false; // stop propagation
        }
      }
    }

    threeJsDom.addGlobalListener('click', onClick, 1);
    return () => void threeJsDom.removeGlobalListener('click', onClick);
  }, [dispatch, selectedJointInfo, webbingInfo]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.code === 'Escape' && selectedJointInfo) {
        setSelectedJointInfo(null);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedJointInfo]);

  const calculatedWebJointVisuals = webbingInfo
    ? webbingInfo.webbingRegions
        .flatMap((webbingRegion) => webbingRegion.calculatedWebJoints)
        .map((webJoint) => <CalculatedWebJointVisual key={webJoint.guid} position={webJoint.location} />)
    : null;

  const webSegmentVisuals = webbingInfo
    ? webbingInfo.webbingRegions.flatMap((webbingRegion) =>
        webbingRegion.webSegments
          .map((webSegment) => {
            const baseJoint = webbingRegion.calculatedWebJoints.find(
              (joint) => joint.guid === webSegment.baseJointGuid,
            );
            const endJoint = webbingRegion.calculatedWebJoints.find((joint) => joint.guid === webSegment.endJointGuid);

            if (!baseJoint) {
              console.error(`Failed to find calculated web joint with guid ${webSegment.baseJointGuid}`);
              return null;
            }
            if (!endJoint) {
              console.error(`Failed to find calculated web joint with guid ${webSegment.endJointGuid}`);
              return null;
            }

            return (
              <WebSegmentVisual
                key={webSegment.guid}
                webSegment={webSegment}
                baseJoint={baseJoint}
                endJoint={endJoint}
              />
            );
          })
          .filter(notNullish),
      )
    : null;

  const webSegmentPreviewVisual = selectedJointInfo ? (
    <WebSegmentPreviewVisual basePoint={selectedJointInfo.joint.location} />
  ) : null;

  return (
    <>
      {calculatedWebJointVisuals}
      {webSegmentVisuals}
      {webSegmentPreviewVisual}
    </>
  );
}
